import * as THREE from "three";
import { Context } from "../core/context";
import { DBEntity, DbLine, DbMesh } from "@/CadEngine";
import {
  ModifyOperateState,
  DrawOperationMovePointBeforeParams,
  DrawOperationMovePointParams,
  IModifyOperation,
} from "./IModifyOperation";
import { ULabel } from "@/ui/ULabel";
import {
  BreakModifyReturnData,
  CommonColor,
  Linearity,
  SampleLayerType,
} from "@/core/Parameters";

class BreakModify implements IModifyOperation {
  private m_context: Context;
  private m_select: DbLine | DbMesh | undefined;
  private m_label: ULabel;
  private m_breakPoints: THREE.Vector3[] = [];
  private m_auxLine: THREE.Line | undefined;
  private m_points: THREE.Vector3[] = [];
  private m_curPoint: THREE.Vector3 = new THREE.Vector3();
  private m_observationLine: THREE.Line | undefined;
  private m_callbackUnderway: (point: THREE.Vector3) => void;
  private m_linePoints: THREE.Vector3[] = [];
  private m_returnData: BreakModifyReturnData = {
    delData: [],
    addData: [],
    breakInfo: [],
  };
  private m_locationObject: THREE.Line;
  private m_accordLayerNames: string[] = [];
  private m_selectLayerLineTypeName: Linearity | undefined;

  constructor(
    context: Context,
    callbackUnderway: (point: THREE.Vector3) => void,
    accordLayerNames: string[]
  ) {
    this.m_context = context;
    this.m_callbackUnderway = callbackUnderway;
    this.m_label = this.m_context.ModifyManager.TextLabel;
    this.m_auxLine = this.m_context.ToolsManager.CreatedAuxLine();
    this.m_locationObject = context.ToolsManager.CreatedLocationLine();
    this.createObservationLine();
    this.m_accordLayerNames = accordLayerNames;
  }

  OnMovePointBefore(params: DrawOperationMovePointBeforeParams): void {}
  OnMovePoint(point: DrawOperationMovePointParams): void {
    this.setSelect();
    let textLabelPosition = point.point.clone();
    this.m_curPoint = point.point.clone();
    this.m_locationObject.position.copy(this.m_curPoint);
    if (this.m_points.length >= 2) return;
    textLabelPosition.x += 30 * this.m_context.SelectionManager.Scale;
    textLabelPosition.y -= 10 * this.m_context.SelectionManager.Scale;
    if (!this.m_points.length) {
      this.m_label.SetVaule(
        "请点取第一个点",
        textLabelPosition,
        new THREE.Vector3(-1, 0, 0)
      );
    } else if (this.m_points.length == 1) {
      this.m_label.SetVaule(
        "请点取第二个点",
        textLabelPosition,
        new THREE.Vector3(-1, 0, 0)
      );
      this.m_auxLine!.geometry.setFromPoints([
        this.m_points[0],
        this.m_curPoint,
      ]);
      this.m_auxLine!.computeLineDistances();
      this.updateObservationLine();
    }
    this.m_label.Visibility = true;
  }
  OnDownPoint(point: DrawOperationMovePointParams): void {
    let { obejct } = point;
    if (this.m_points.length == 1) {
      this.m_points.push(point.point.clone());
      this.m_callbackUnderway(point.point.clone());
      this.endCurHandle();
      return;
    }
    if (obejct?.length) {
      let obj = obejct[0];
      if (obj instanceof DbLine || obj instanceof DbMesh) {
        let estimateResult =
          this.m_context.ToolsManager.VerifyLayerNameOrDataType(
            obj,
            this.m_accordLayerNames
          );
        if (estimateResult) {
          this.m_selectLayerLineTypeName =
            this.m_context.ToolsManager.GetLayerLineTypeName(obj);
          this.m_select = obj;
          this.m_points.push(point.point.clone());
          if (this.m_select) {
            if (this.m_select instanceof DbLine && this.m_select.points) {
              let startP = new THREE.Vector3(
                this.m_select.points[0],
                this.m_select.points[1],
                this.m_select.points[2]
              );
              let endP = new THREE.Vector3(
                this.m_select.points.at(-3),
                this.m_select.points.at(-2),
                this.m_select.points.at(-1)
              );
              this.m_linePoints = [startP, endP];
            } else if (
              this.m_select instanceof DbMesh &&
              this.m_select.vertexs
            ) {
            }
          }
        }
      }
    }
  }
  onKeyDown(e: KeyboardEvent): void {}
  onKeyUp(e: KeyboardEvent): void {}

  OnZoom(camera: THREE.Camera): void {}
  OnWheel(e: WheelEvent): void {}

  onMouseMove(e: PointerEvent): void {}
  onMouseDown(e: PointerEvent): void {}
  onMouseUp(e: PointerEvent): void {}

  Finish(operate: ModifyOperateState) {
    this.clear();
    if (operate == ModifyOperateState.complete) {
      return this.m_returnData;
    } else if (operate == ModifyOperateState.cancel) {
      return [];
    }
  }

  private setSelect() {
    if (this.m_select) {
      this.m_context.SelectionManager.ClearSelected();
      this.m_context.SelectionManager.SetSelected(this.m_select);
    }
  }
  private createObservationLine() {
    const geometry = new THREE.BufferGeometry();
    const matLine = new THREE.LineBasicMaterial({
      color: CommonColor.Gray,
    });
    this.m_observationLine = new THREE.LineSegments(geometry, matLine);
    this.m_observationLine.frustumCulled = false;
    this.m_observationLine.position.z = 10;
    this.m_context.SceneManager.AuxModel.add(this.m_observationLine);
  }

  private updateObservationLine() {
    if (this.m_select && this.m_select.points) {
      let observationLineEndPoint = new THREE.Vector3();
      let line3 = new THREE.Line3(this.m_linePoints[0], this.m_linePoints[1]);
      line3.closestPointToPoint(this.m_curPoint, true, observationLineEndPoint);
      let dis1 = this.m_points[0].distanceTo(this.m_linePoints[1]);
      let dis2 = observationLineEndPoint.distanceTo(this.m_linePoints[1]);
      this.m_breakPoints = [this.m_points[0], observationLineEndPoint];
      if (dis1 < dis2) {
        this.m_breakPoints = [observationLineEndPoint, this.m_points[0]];
      }
      if (this.m_selectLayerLineTypeName == Linearity.Dash) {
        // let breakPoints =
        //   this.m_context.ToolsManager.PassStartPointAndEndPointGetSelectPoint(
        //     this.m_select!,
        //     this.m_breakPoints[0],
        //     this.m_breakPoints[1]
        //   );
        // this.m_breakPoints.length = 0;
        // this.m_breakPoints = breakPoints;
      }
      this.m_observationLine?.geometry.setFromPoints(this.m_breakPoints);
    }
  }

  private clear() {
    this.m_context.ToolsManager.ObjectsClear([
      this.m_locationObject,
      this.m_auxLine!,
      this.m_observationLine!,
    ]);
  }

  private removeDuplicates(datas: THREE.Vector3[]) {
    for (let i = 0; i < datas.length - 1; i++) {
      let curData = datas[i];
      let nextData = datas[i + 1];
      if (curData.equals(nextData)) {
        datas.splice(i, 2);
        i--;
      }
    }
  }

  private endCurHandle() {
    let addPoints = [];
    if (this.m_breakPoints.length && this.m_select) {
      let layer = this.m_context.ModelManager.GetLayerByIdColor(
        this.m_select.layerId,
        this.m_select.color!
      );
      if (!layer) return;
      if (this.m_select instanceof DbLine) {
        if (this.m_selectLayerLineTypeName == Linearity.Dash) {
          let breakStartPoint = this.m_breakPoints[0];
          let breakEndPoint = this.m_breakPoints[this.m_breakPoints.length - 1];
          let dashPoints1 =
            this.m_context.ToolsManager.PassStartPointAndEndPointGetSelectPoint(
              this.m_select!,
              this.m_linePoints[0],
              breakStartPoint
            );
          dashPoints1[0] = this.m_linePoints[0];
          dashPoints1[dashPoints1.length - 1] = breakStartPoint;
          let dashPoints2 =
            this.m_context.ToolsManager.PassStartPointAndEndPointGetSelectPoint(
              this.m_select!,
              breakEndPoint,
              this.m_linePoints[1]
            );
          dashPoints2[0] = breakEndPoint;
          dashPoints2[dashPoints2.length - 1] = this.m_linePoints[1];
          addPoints = [dashPoints1, dashPoints2];
          for (let i = 0; i < addPoints.length; i++) {
            const addPoint = addPoints[i];
            let addLine = this.m_context.ModelManager.AddCustomLines(
              layer.Name,
              this.m_select!.color!,
              addPoint,
              false
            )[0];
            if (!this.m_select.userData?.originData) {
              addLine.userData = { originData: this.m_select };
            } else {
              addLine.userData = this.m_select.userData;
            }
            this.m_returnData.addData.push(addLine);
          }
        } else {
          addPoints = [
            this.m_linePoints[0],
            this.m_breakPoints[0],
            this.m_breakPoints[1],
            this.m_linePoints[1],
          ];
          if (
            this.m_breakPoints[0].distanceTo(this.m_linePoints[0]) >=
            this.m_breakPoints[1].distanceTo(this.m_linePoints[0])
          ) {
            addPoints = [
              this.m_linePoints[0],
              this.m_breakPoints[1],
              this.m_breakPoints[0],
              this.m_linePoints[1],
            ];
          }
          this.removeDuplicates(addPoints);
          for (let i = 0; i < addPoints.length; i += 2) {
            let points = [addPoints[i], addPoints[i + 1]];
            let addLine = this.m_context.ModelManager.AddCustomLines(
              layer.Name,
              this.m_select!.color!,
              points
            )[0];
            if (!this.m_select.userData?.originData) {
              addLine.userData = { originData: this.m_select };
            } else {
              addLine.userData = this.m_select.userData;
            }
            this.m_returnData.addData.push(addLine);
          }
        }
      }

      this.m_returnData.delData.push(this.m_select as DbLine);
      this.m_returnData.breakInfo.push({
        breakPoints: this.m_breakPoints,
        extendId:
          this.m_select.userData?.originData?.extend_id ??
          this.m_select.extend_id,
      });
      this.m_context.ModelManager.DeleteEntity(this.m_select!);
      this.m_observationLine?.geometry.setFromPoints([]);
      this.m_auxLine?.geometry.setFromPoints([]);
      this.m_select = undefined;
      this.m_context.SelectionManager.ClearSelected();
      this.m_points.length = 0;
    }
  }
}

export { BreakModify };
